
import { Paper } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DataGrid, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector, GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid";
import { useMemo } from "react";
import { rtfoConversionFactors } from "./rtfoConversionFactors";

const useStyles = makeStyles({
  root: {
    border: 0,
    color: 'text.primary',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent'
  },
  tableHeading: {
    marginLeft: 10,
    marginTop: 10,
  },
  table: {
    overflow: 'auto',
    marginTop: 0,
    marginBottom: 0,
    border: 'none'
  },
});

export const RTFOEventsDetailsDataTable = ({ currentRTFO, darkMode }) => {

  
  const units = currentRTFO?.settings?.units || 'metric';

    // STYLES
    const classes = useStyles();

    const rows = useMemo(() => {
      if (!currentRTFO?.stages?.length > 0) return [];

      const data = [];

      for (const stageName of [...currentRTFO?.stages]?.reverse()) {
        if (!currentRTFO?.stageData?.[stageName]?.events) continue;
        for (const eventType of Object.keys(currentRTFO?.stageData?.[stageName]?.events)) {
          const events = currentRTFO?.stageData?.[stageName]?.events?.[eventType];
          for (const eventIndex in events) {
            const event = events[eventIndex];

            data.push({
              id: `${stageName}-${eventType}-${eventIndex}`,
              stageName: stageName,
              wellName: stageName?.split('_')?.[1],
              stageNumber: stageName?.split('_')?.[2],
              eventType: formatEventType(eventType),
              eventName: `${formatEventType(eventType)} ${+eventIndex+1}`,
              timestamp: new Date(event.timestamp)?.toLocaleString(),
              pressure: Number((event.pressure * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(3)) || 0,
              rate: Number((event?.rate * (units === 'field' ? rtfoConversionFactors.rate : 1))?.toFixed(2)) || 0,
              slurryVolume: Number((event?.slurryVolume * (units === 'field' ? rtfoConversionFactors.slurryVolume : 1))?.toFixed(2)) || 0,
              cleanVolume: Number((event?.cleanVolume * (units === 'field' ? rtfoConversionFactors.cleanVolume : 1))?.toFixed(2)) || 0,
              rnp: Number((event?.rnp * (units === 'field' ? rtfoConversionFactors.rnp / 20 : 1))?.toFixed(3)) || 0,
              tpRate: Number((event?.tpRate * (units === 'field' ? rtfoConversionFactors.tpRate : 1))?.toFixed(3)) || 0,
              mfr: Number((event.mfr * (units === 'field' ? rtfoConversionFactors.mfr : 1))?.toFixed(3)) || 0,
              proppant: Number((event?.proppant * (units === 'field' ? rtfoConversionFactors.proppant : 1))?.toFixed(2)) || 0,
              proppantBH: Number((event?.proppantBH * (units === 'field' ? rtfoConversionFactors.proppantBH : 1))?.toFixed(2)) || 0,
              cumProppant: Number((event.cumProppant * (units === 'field' ? rtfoConversionFactors.totalProppant : 1))?.toFixed(3)) || 0,
              fr: Number((event?.fr * (units === 'field' ? rtfoConversionFactors.fr : 1))?.toFixed(2)) || 0,
              fr2: Number((event?.fr2 * (units === 'field' ? rtfoConversionFactors.fr2 : 1))?.toFixed(2)) || 0,
              dhPressureTop: Number((event?.dhPressureTop * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(3)) || 0,
              dhTemperatureTop: Number(units === 'field' ? rtfoConversionFactors.temperature(event?.dhTemperatureTop) : event?.dhTemperatureTop?.toFixed(2)) || 0,
              dhPressureBottom: Number((event?.dhPressureBottom * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(3)) || 0,
              dhTemperatureBottom: Number(units === 'field' ? rtfoConversionFactors.temperature(event?.dhTemperatureBottom) : event?.dhTemperatureBottom?.toFixed(2)) || 0,
              minPumpingPressure: Number((currentRTFO?.stageData?.[stageName]?.metrics?.minPumpingPressure * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(3)) || 0,
              maxPumpingPressure: Number((currentRTFO?.stageData?.[stageName]?.metrics?.maxPumpingPressure * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(3)) || 0,
              averagePumpingPressure: Number((currentRTFO?.stageData?.[stageName]?.metrics?.averagePumpingPressure * (units === 'field' ? rtfoConversionFactors.pressure : 1))?.toFixed(3)) || 0,
              minPumpingRate: Number((currentRTFO?.stageData?.[stageName]?.metrics?.minPumpingRate * (units === 'field' ? rtfoConversionFactors.rate : 1))?.toFixed(3)) || 0,
              maxPumpingRate: Number((currentRTFO?.stageData?.[stageName]?.metrics?.maxPumpingRate * (units === 'field' ? rtfoConversionFactors.rate : 1))?.toFixed(3)) || 0,
              averagePumpingRate: Number((currentRTFO?.stageData?.[stageName]?.metrics?.averagePumpingRate * (units === 'field' ? rtfoConversionFactors.rate : 1))?.toFixed(3)) || 0,
              preSand: currentRTFO?.stageData?.[stageName]?.events?.preSand?.length || 0,
              breakdown: currentRTFO?.stageData?.[stageName]?.events?.breakdown?.length || 0,
              perfClosure: currentRTFO?.stageData?.[stageName]?.events?.perfClosure?.length || 0,
              plugSlip: (currentRTFO?.stageData?.[stageName]?.events?.plugSlip || currentRTFO?.stageData?.[stageName]?.plugSlips)?.length || 0,
              sandoff: currentRTFO?.stageData?.[stageName]?.events?.sandoff?.length || 0,
              isolationIssue: (currentRTFO?.stageData?.[stageName]?.events?.isolationIssue?.length || 0) + (currentRTFO?.stageData?.[stageName]?.pumpdownParams?.postPerfISIP - currentRTFO?.stageData?.[stageName]?.pumpdownParams?.pumpdownISIP < 1 ? 1 : 0),
              equipmentIssue: currentRTFO?.stageData?.[stageName]?.events?.equipmentIssue?.length || 0,
              lostSuction: currentRTFO?.stageData?.[stageName]?.events?.lostSuction?.length || 0,
              other: currentRTFO?.stageData?.[stageName]?.events?.other?.length || 0,
              fracfeed: currentRTFO?.stageData?.[stageName]?.events?.fracfeed?.length || 0,
              ballSeating: currentRTFO?.stageData?.[stageName]?.events?.ballSeating?.length || 0,
              comments: currentRTFO?.stageData?.[stageName]?.comments?.length || 0
            });
          };
        };
      };

      return data;
     
    }, [currentRTFO?.stageData, currentRTFO?.stages, units]);
  
  try {

    const columns = [
      {
        field: 'stageName',
        headerName: 'Stage Name',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'wellName',
        headerName: 'Well',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'stageNumber',
        headerName: 'Stage No.',
        width: 100,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'eventName',
        headerName: 'Event Name',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'timestamp',
        headerName: 'Time',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'string'
      },
      {
        field: 'pressure',
        headerName: `Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'rate',
        headerName: `Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'slurryVolume',
        headerName: `Slurry Volume (${units === 'field' ? 'bbl' : 'm3'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'cleanVolume',
        headerName: `Clean Volume (${units === 'field' ? 'bbl' : 'm3'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'rnp',
        headerName: 'Fracfeed',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'tpRate',
        headerName: `TP Rate (${units === 'field' ? 'bbl/min' : 'L/min'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'mfr',
        headerName: `MFR/Cluster (${units === 'field' ? 'lb/s' : 'kg/s'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'proppant',
        headerName: `Propp. Surf. Conc. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'proppantBH',
        headerName: `Propp. BH Conc. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'cumProppant',
        headerName: `Cum. Propp. (${units === 'field' ? 'lb' : 'ton'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'fr',
        headerName: `DFR Conc. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'fr2',
        headerName: `LFR Conc. (${units === 'field' ? 'gal/mgal' : 'L/m3'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'dhPressureTop',
        headerName: `BHP Top (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'dhPressureBottom',
        headerName: `BHP Bottom (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'dhTemperatureTop',
        headerName: `BHT Top (${units === 'field' ? '°F' : '°C'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'dhTemperatureBottom',
        headerName: `BHT Bottom (${units === 'field' ? '°F' : '°C'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'minPumpingPressure',
        headerName: `Min Pumping Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'maxPumpingPressure',
        headerName: `Max Pumping Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'averagePumpingPressure',
        headerName: `Average Pumping Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'minPumpingRate',
        headerName: `Min Pumping Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'maxPumpingRate',
        headerName: `Max Pumping Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'averagePumpingRate',
        headerName: `Average Pumping Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'preSand',
        headerName: 'Pre Sand',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'breakdown',
        headerName: 'Breakdown',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'perfClosure',
        headerName: 'Perf Closure',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'plugSlip',
        headerName: 'Plug Slips',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'sandoff',
        headerName: 'Sandoff',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'isolationIssue',
        headerName: 'Isolation Issue',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'equipmentIssue',
        headerName: 'Equipment Issue',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'lostSuction',
        headerName: 'Lost Suction',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'other',
        headerName: 'Other Event',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'fracfeed',
        headerName: 'Fracfeed Event',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'ballSeating',
        headerName: 'Ball Seating Event',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      },
      {
        field: 'comments',
        headerName: 'Comments',
        width: 150,
        editable: false,
        sortable: true,
        filterable: true
      }
    ];

    return (
      <Paper className={classes.root} elevation={0}>
        <DataGrid
          rows={rows}
          columns={columns}
          rowsPerPageOptions={[10,25,50,100]}
          disableSelectionOnClick
          className={classes.table}
          sx={{
            '& .MuiDataGrid-row:nth-of-type(odd)': {
              backgroundColor: darkMode ? '#263238' : 'whitesmoke'
            },
            '& .super-app-theme--Current': {
              fontWeight: 'bold',
              backgroundColor: 'rgba(7, 88, 183, 0.25)' // highlight the current row
            }
          }}
          components={{
            Toolbar: CustomToolbar,
          }}
          density="compact"
          style={{fontSize: '12px'}}
          loading={currentRTFO?.isLoadingData}
          getRowClassName={(params) => {
            return (params?.row?.stageName === currentRTFO?.currentStageName) ? `super-app-theme--Current` : '';
          }}
        />
      </Paper>
    );
  } catch (error) {
    return <></>
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarFilterButton style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarDensitySelector style={{ color: darkMode && 'lightgrey' }} />
        <GridToolbarExport style={{ color: darkMode && 'lightgrey' }} />
      </GridToolbarContainer>
    );
  };

  function formatEventType(eventType) {
    switch(eventType) {
      case 'preSand':
        return 'Pre Sand';
      case 'Breakdown':
        return 'Breakdown';
      case 'perfClosure':
        return 'Rate Drop';
      case 'plugSlip':
        return 'Plug Slip';
      case 'sandoff':
        return 'Sandoff';
      case 'isolationIssue':
        return 'Isolation Issue';
      case 'equipmentIssue':
        return 'Equipment Issue';
      case 'lostSuction':
        return 'Lost Suction';
      case 'ballSeating':
        return 'Ball Seating';
      case 'other':
        return 'Other Event';
      case 'fracfeed':
        return 'Fracfeed Event';
      default:
        return eventType;
    };
  };
};

